var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-ai-chat> — the transcript and the composer.
 *
 * DELIBERATELY PLAIN. This is the plumbing surface: it proves a turn runs from a real tab against a
 * real session, and it is meant to be redesigned. Everything here is tokens and flexbox, no
 * bespoke visual language, so replacing the markup is a rewrite of `render()` and nothing else.
 *
 * WHAT IT IS SAFE TO CHANGE: all of it. The component holds NO state — it reads `conversation.turns`
 * and `conversation.status`, calls `conversation.send()`, and re-renders on `change`. Rewriting the
 * markup, swapping in base-ui components, restructuring the layout: none of that can affect how a
 * turn runs, because none of the logic is here (see `conversation.ts`).
 *
 * WHAT TO KEEP WHEN YOU REDESIGN, because each is a behaviour rather than a style:
 *   • The composer is disabled while `busy` — otherwise a second turn races the first.
 *   • A turn's `error` renders as PROSE, not a code. Every failure on this platform explains itself.
 *   • Enter sends, Shift+Enter newlines. Anything else surprises people mid-sentence.
 *   • The transcript sticks to the bottom only when already at the bottom, so an arriving reply
 *     never yanks the view away from someone reading back (`no unpurposeful layout shift`).
 */
import { cancelWait } from './run-tree';
import { isWaitTool } from './toolname';
import { LitElement, html, nothing } from 'lit';
import { readDraft, saveDraft } from './session-memory';
import { greetingView, greetingCss } from './greeting-view';
import { rootRunIdOfTool, spawnedRunIdOfTool } from './manager';
import { RunStatus } from '@vitesse/proto/vitesse/ai/v1/agent_pb.js';
import { greetingSeed } from './greeting';
import './chapters-menu';
import { continueList, listMarkerAt } from './composer-text';
import { ACCEPT_ATTR, stagePicked } from './attachments';
import { chipStyles, renderChips } from './attachment-chips';
import { customElement, property, query, state } from 'lit/decorators.js';
import { css } from 'lit';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { ref } from 'lit/directives/ref.js';
import { VirtualRows } from '../virtual-rows';
import { liveThread } from './live-thread';
import { toolDisplayName } from './toolname';
import { FOLD_MS, turnStyles, renderTurn as renderTurnShared, transcriptSkeleton } from './turn-view';
import { conversationLabel } from './conversation-list';
import { renderMarkdown } from './markdown';
import { headLead, headLeadCss } from './head-lead';
import { runStateLabel, fmtUsd } from './run-format';
import { REALTIME_MODELS, TEXT_MODELS, IMAGE_MODELS, catalogEvents } from './model-catalog';
import './tools-menu';
import { backButton, iconButton } from '../elements/shared';
import { statusBadge, statusBadgeStyles } from '../components/templates';
import '../elements/vitesse-icon';
import '../elements/vitesse-button';
/**
 * Turns above which the transcript is windowed.
 *
 * Chosen so a normal working thread never touches the windowed path: most conversations sit well
 * under this, and the ones that do not are exactly the ones where rendering everything hurts.
 */
const VIRTUALIZE_ABOVE = 30;
let VitesseAiChat = class VitesseAiChat extends LitElement {
    constructor() {
        super(...arguments);
        /** Straight back to the conversations list, from any depth. Absent hides the control. */
        /** The org's specialists, from the handshake roster. Empty hides the picker —
         *  a chooser with one option is a decision nobody has. */
        this.specialists = [];
        /** The thread's title. A fresh conversation has none until the server names it, which is why the
         *  empty case falls back rather than rendering a blank heading. */
        this.convTitle = '';
        /** The person, for the greeting an empty thread shows. Absent is fine - the rotation has an
         *  anonymous form of every line. */
        this.userName = '';
        /** Ask-tier calls waiting on the user, rendered as inline approval cards at the foot of the
         *  transcript. State lives on the manager; this is a pure render of it, like everything here. */
        this.confirms = [];
        /** The org's defaults, shown as each picker's placeholder so "inherit" is legible rather than
         *  blank. Without these the menu reads as though nothing is selected mid-conversation, while the
         *  opening composer — which passes them — correctly shows the inherited name. */
        this.defaultChatModel = '';
        this.defaultVoiceModel = '';
        /** The org's IMAGE default, for the third row in the model menu. Empty on a surface with no
         *  image capability, which is what makes that row disappear rather than render blank. */
        this.defaultImageModel = '';
        /**
         * WHICH NODE THIS THREAD IS, on the canvas.
         *
         * A run id for an agent's thread; '' for a person's, which is the conversation card. It is what
         * lets the canvas open ON the thread you were reading rather than cutting to a framed forest with
         * nothing saying where you were.
         */
        this.nodeRunId = '';
        /**
         * The tree this thread belongs to, when the host knows it.
         *
         * A person's thread finds its own by looking for a spawn among its turns. An AGENT's thread
         * cannot: a leaf agent spawned nothing, so it had no way onto the canvas at all — the one surface
         * where "what else is running beside me" is answerable. Any run id addresses its whole forest, so
         * the host passes the run and the button appears everywhere.
         */
        this.treeRoot = '';
        /**
         * AN AGENT'S ANSWER, MID-SENTENCE.
         *
         * Set only on a RUN's thread, and only while that run is unfinished: the worker publishes what
         * the model has written so far onto the run, and this paints it as a turn that does not exist in
         * the archive yet. The moment the step lands, the durable message arrives through the ordinary
         * path and this goes empty — one thing on screen at a time, never both.
         */
        this.streaming = { text: '', reasoning: '', tools: [], parts: [] };
        /*
         * THE TRANSCRIPT IS WINDOWED ABOVE A THRESHOLD, AND WHOLE BELOW IT.
         *
         * A turn is not a log line: it can be a sentence or a table plus a thinking disclosure plus an
         * agent tree, so this runs MEASURED. Every turn rendered on every update is fine at twenty and is
         * not fine at five hundred — the DOM keeps growing, each update walks all of it, and a long
         * working session gets slower the more useful it has been.
         *
         * Below the threshold nothing is windowed at all. Short threads are the overwhelming majority,
         * they cost nothing to render whole, and leaving them on the simple path means the streaming and
         * stick-to-bottom behaviour that people see every day is not riding on windowing arithmetic.
         * Virtualising is a fix for the long tail, and it should not be able to break the short one.
         */
        this.vrows = new VirtualRows(this, { estimateSize: 150, overscan: 6, measured: true });
        /** The thread's chapters, for the contents button. Empty ⇒ no button, which is the right answer
         *  for a short thread: a contents page that opens to say "nothing here" is worse than none. */
        this.chapters = [];
        /** The message being re-asked, while its text sits in the composer. Null the rest of the time. */
        this.pendingBranch = null;
        /**
         * Files staged for the NEXT message, mid-conversation.
         *
         * The chat owns its picker rather than asking the host for one: the attach button used to call
         * an `onAttach` no host ever bound, so the button was hidden and a file could only be attached
         * to the first message of a thread. Same screening and the same chips as the opening composer.
         */
        this.files = [];
        this.rejected = '';
        this.draft = '';
        /** True once the inline title has been scrolled past — see the sticky header below. */
        this.scrolled = false;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
        /** Fixed for the life of this mount: a greeting that reshuffled per render would be motion
         *  nobody asked for. Same reasoning as the composer's. */
        this.greetSeed = greetingSeed();
        /** Which app this panel belongs to, for per-surface storage. Absent ⇒ nothing is persisted. */
        this.surface = '';
        this.onChange = () => this.requestUpdate();
        this.stickToBottom = true;
        /**
         * HOW MUCH ROOM THIS THREAD IS BEING GIVEN — `full` when it is the one being worked in, `context`
         * when it sits beside that one in the panel's column stack.
         *
         * It changes ONE thing: a live thought does not open itself (see `TurnHandlers.quiet`). Everything
         * else is identical, and deliberately so — a column the reader can look at but not use is not a
         * column, it is a screenshot. Every disclosure still opens on a click, the composer is still
         * there, and nothing needs resizing or reordering first.
         */
        this.density = 'full';
        /** Which tool rows are open, by call id. */
        this.openTools = new Set();
        /*
         * THE CATALOG ARRIVES AFTER THIS RENDERS, and this has to hear about it.
         *
         * `TEXT_MODELS` / `REALTIME_MODELS` are filled IN PLACE when the fetch lands, so nothing about
         * them changes identity and Lit has no reason to re-render. The opening composer subscribes to
         * the change event; the mid-conversation dock did not — so resuming a thread showed two pickers
         * with no models in them, no model name, and no effort control (which is gated on knowing whether
         * the effective model can reason). The chat had neither default, so both pickers fell back to
         * their placeholders mid-conversation and the thinking row never rendered.
         */
        this.onCatalog = () => this.requestUpdate();
        /**
         * Stay at the bottom WHILE something is still changing height.
         *
         * A single pin cannot hold through an animation: it runs once, at the start, against a height the
         * card has not reached yet — so the transcript is left slightly off and the remaining 240ms of
         * growth reads as a jump. This follows for exactly as long as the fold takes (FOLD_MS, shared with
         * the CSS so the two cannot drift), and stops the moment the reader scrolls away from the bottom.
         *
         * Only when they were AT the bottom. Following someone who is reading history would be the layout
         * shift the rule above exists to prevent.
         */
        this.followUntil = 0;
        this.following = false;
        /*
         * STICK-TO-BOTTOM FOLLOWS THE READER'S HAND, NOT THE LAYOUT.
         *
         * `stickToBottom` was recomputed on EVERY scroll event, and a scroll event is not only a person
         * scrolling: a watched thread swaps its whole turn list every two seconds, the windowed
         * transcript re-measures, a fold opens — each can move `scrollTop` for a frame, and a frame in
         * which the content is momentarily short reads as "at the bottom", re-arms the stick, and the
         * next repaint yanks the reader back down. Reported as the transcript fighting them while they
         * scrolled up.
         *
         * So the flag changes only on a scroll the reader CAUSED — wheel, touch, or a scrolling key
         * within the last moment. Layout-driven events can neither arm nor release it.
         */
        this.lastHandAt = 0;
        this.lastTouchY = 0;
        this.onHand = (e) => {
            this.lastHandAt = performance.now();
            /*
             * ANY UPWARD MOVE OF THE HAND RELEASES THE STICK, at once and without a threshold.
             *
             * The release used to be decided by `onScroll` from where the view ENDED UP: still within 40px
             * of the bottom meant still stuck. While a live thread grows, one wheel tick moves the view up
             * by less than that, the next render pins it straight back down, and the only way out was to
             * scroll FAST enough to clear 40px inside a single tick — reported as the transcript bouncing
             * the reader back to the bottom unless they fought it. Intent is not a distance: a person who
             * moved the view up wants to read what is above, however far they got.
             */
            let up = false;
            if (e instanceof WheelEvent)
                up = e.deltaY < 0;
            else if (e instanceof KeyboardEvent)
                up = e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'Home';
            else if (e.type === 'touchstart' || e.type === 'touchmove') {
                const y = e.touches[0]?.clientY ?? this.lastTouchY;
                // A finger moving DOWN the glass scrolls the content UP.
                up = e.type === 'touchmove' && y > this.lastTouchY + 1;
                this.lastTouchY = y;
            }
            if (up && this.stickToBottom) {
                this.stickToBottom = false;
                const log = e.currentTarget;
                if (log)
                    this.captureAnchor(log);
            }
        };
        this.onScroll = (e) => {
            const el = e.currentTarget;
            if (performance.now() - this.lastHandAt < 250) {
                this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
                this.captureAnchor(el);
            }
            // The sticky header appears once the inline title has been carried up out of view — the same
            // trigger the base pages use, so the two behave identically.
            this.scrolled = el.scrollTop > 34;
        };
        this.onKey = (e) => {
            if (e.key !== 'Enter')
                return;
            const ta = e.target;
            /*
             * SHIFT-ENTER MAKES A NEW LINE — and if that line is inside a list, it continues the list.
             *
             * The markdown is rendered now, so people write lists; typing the next "- " by hand every time
             * is the bookkeeping every editor worth using does for you. Enter alone still sends: this only
             * changes what a deliberate newline does.
             */
            if (e.shiftKey) {
                const m = listMarkerAt(ta.value, ta.selectionStart ?? ta.value.length);
                if (!m)
                    return; // ordinary newline
                e.preventDefault();
                const next = continueList(ta.value, ta.selectionStart ?? ta.value.length, m);
                this.setDraft(next.text);
                void this.updateComplete.then(() => {
                    ta.setSelectionRange(next.caret, next.caret);
                });
                return;
            }
            e.preventDefault();
            void this.submit();
        };
        this.onPick = (e) => {
            const el = e.target;
            ({ files: this.files, rejected: this.rejected } = stagePicked(this.files, [...(el.files ?? [])], t));
            el.value = ''; // so the same file can be re-picked after being removed
        };
        /**
         * The way into what a spawn started: one control, on the row that started it.
         *
         * What was here was the tree itself, drawn under the call — a live row reading
         * "scxml-author using symbols.search 3m02s", with the driver's lease refusal above it in red.
         * Two problems, and the second is the worse one. The tree is a SURFACE, and re-drawing a small
         * unstyled copy of it inside a disclosure meant the reader met it twice in two shapes. And
         * `driveError` is not an error the reader has anything to do with: "this agent tree is already
         * being driven" is ordinary lease contention — a second driver being told to follow along
         * read-only, which is the system working — rendered as a failure under their question.
         *
         * So neither is drawn. The row carries a link to the agent, and the tree is the tree.
         */
        /** Waits being cancelled right now, so a second press cannot send a second cancel. */
        this.cancellingWaits = new Set();
    }
    /*
     * SHADOW DOM, like every other base-ui element.
     *
     * This rendered into the LIGHT DOM while keeping its CSS in `static styles` — and Lit only
     * installs `static styles` on a SHADOW root. So all four blocks below were silently dropped and
     * the header, its badges and the delete control rendered as bare text on the page. Nothing
     * errored: the element worked, it was simply unstyled, which is why it survived a review.
     *
     * The light-DOM opt-out was there so "app tokens and SCSS resolve without piercing". Tokens are
     * custom properties and inherit through a shadow boundary unchanged, and this template uses only
     * its own classes plus the three shared base-ui blocks imported above — it reaches for no app
     * SCSS. So there was nothing to gain and a stylesheet to lose. (Contrast `panel.ts`, which stays
     * in the light DOM ON PURPOSE and therefore ships its CSS as a <style> in its template.)
     */
    static { this.styles = [
        headLeadCss,
        backButton,
        iconButton,
        chipStyles,
        statusBadgeStyles,
        turnStyles,
        greetingCss,
        css `
      /* THE BRIEF — the job, in the position the reader's own opening message would hold, and drawn
         as a message: .turn + .bubble, the same pair every other message in the thread uses. It was
         a ruled quotation block of its own, which made the one thing that IS a message in this
         position look like the only thing that is not.
         LEFT, not .mine: the reader did not write it. The byline says who did. */
      .brief {
        margin-bottom: 14px;
      }
      .brief-sig {
        display: flex;
        align-items: center;
        gap: 5px;
        /* The AI accent, which is what this colour means everywhere else in the product — so a
           glance answers "who is speaking" before a word of it is read. */
        color: var(--ai);
        font-size: 11px;
        margin-bottom: 3px;
      }
      .brief-body {
        color: var(--text-primary);
      }
      .brief-deliv {
        margin-top: 6px;
        padding-top: 6px;
        border-top: 1px solid var(--border-color);
        color: var(--text-secondary);
      }
    `,
        css `
      :host {
        container-type: inline-size;
        display: flex;
        flex-direction: column;
        min-height: 0;
        flex: 1;
      }
      /* flex:1 + min-height:0, NOT height:100% — a percentage height needs a definite parent, and
         one auto-sized ancestor anywhere above turns the whole column auto, at which point .log
         grows with its content instead of scrolling inside it. That is why a long thread ran off
         the bottom. */
      .shell {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        position: relative; /* the sticky header is positioned against this, not the page */
      }

      /* ── the header, inline and sticky ── */
      /* 52px is the base pages' header height, so the rail's three surfaces and every page in the
         app put their back control on the same line. */
      .head {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        box-sizing: border-box;
        height: 52px;
        padding: 0 12px;
        flex-shrink: 0;
      }
      /* The name does NOT take the free space — the spacer does. The badges are measurements OF
         this thread, so they belong beside its name; pushed to the far end they sat against Delete
         and read as controls rather than as facts about the title next to them. Its own rules,
         including the min-width floor that stops badges squeezing it away, live in head-lead. */
      .head.compact .head-name {
        font-size: 14px;
      }
      /* Lifted out of a style attribute so the composer is themed like the rest of the app. */
      .dock input[type='file'] {
        display: none;
      }
      /* The chips sit above the box, inside the same column, so they leave with the message and
         never widen the dock. */
      .draftwrap {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .draft {
        width: 100%;
        /*
         * AS TALL AS WHAT IT SITS BESIDE.
         *
         * One line of input next to a two-row stack of controls left the dock mostly empty and the
         * box looking like an afterthought in it. The floor is the rail's own height — the primary
         * button, the row above it and the gap between — so the two halves of the dock are one
         * block. It still GROWS from here as the person types; the autosize routine never shrinks
         * it below a floor it does not set.
         */
        min-height: 74px;
        /* Border-box, or the padding pushes the box past its column and the primary button on the
           rail draws over its right edge. */
        box-sizing: border-box;
        resize: none;
        /* The cap. autosizeDraft() sets the height from measured content; this is what stops it —
           a composer that grew without limit would eventually own the panel and push the
           conversation it belongs to off the top. Past this it scrolls, which needs no explaining.
           Roughly eight rows — enough to see a paragraph whole, short enough that the thread it is
           part of is still the larger half of the panel. */
        max-height: 168px;
        overflow-y: auto;
        padding: 8px 10px;
        border-radius: 8px;
        font: inherit;
        font-size: 14px;
        background: var(--bg-secondary);
        color: var(--text-primary);
        border: 1px solid var(--border-color);
      }
      /*
       * WHAT SURVIVES A NARROW PANEL.
       *
       * The rail is drag-resizable, and at its narrowest the header has to choose. The order is by
       * what you cannot do without: Back (you would be stuck), Delete (the only action here), and
       * the title (which thread this is). The badges are measurements — worth having, never worth
       * displacing a control — so they go first, cost first.
       *
       * A container query rather than a viewport one: what matters is how wide the PANEL is, and the
       * panel is 360px in a 2560px window as often as not.
       */
      /*
       * The badges go in order of least-missed, and they go EARLY — before the title has to give up
       * anything. The floor above stops the title shrinking; these make room for it to keep.
       *
       * nth-child INSIDE THE GROUP, not nth-of-type on the row. The badges are spans and so are the
       * title and the spacer, so nth-of-type was counting those too — every threshold fired one or
       * two badges earlier than it reads, which is why the numbers looked generous and the row
       * still overflowed. Counted inside their own box, n means the nth badge.
       *
       * The widths are what the row actually costs: ~278px of chevron, title floor, controls and
       * padding, plus ~80px per badge.
       */
      }
      /* Sits directly on top of the composer, sharing its edge — it is a state OF the box below it,
         not a message about the conversation. */
      .branching {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 5px 12px;
        border-top: 1px solid var(--border-color);
        background: color-mix(in srgb, var(--ai) 8%, transparent);
        color: var(--text-secondary);
        font-size: 12px;
      }
      .branching vitesse-icon {
        color: var(--ai);
        flex: none;
      }
      .branching span {
        flex: 1;
        min-width: 0;
      }
      .branch-cancel {
        background: none;
        border: 0;
        padding: 0;
        font: inherit;
        color: var(--text-tertiary);
        cursor: pointer;
      }
      .branch-cancel:hover {
        color: var(--ai);
      }
      .head-spacer {
        flex: 1;
        min-width: 0;
      }
      /* THE BACKSTOP. Whatever the breakpoints do or fail to do, this group yields before the
         controls beside it do — so Delete is never pushed outside the panel again. */
      .head-metrics {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
      }
      .head .badge {
        flex-shrink: 0;
      }
      .head .badge .base-status-badge {
        font-variant-numeric: tabular-nums;
      }
      .head .del {
        flex-shrink: 0;
        color: var(--text-tertiary);
      }
      /* The thread's name, in the base pages' title treatment — scaled for a rail, which is
         narrower than any page this pattern was drawn for. */
      .title {
        margin: 4px 0 14px;
        font-size: 18px;
        font-weight: 500;
        line-height: 1.25;
        color: var(--text-primary);
      }
      /* Parked above the top edge, slid down on scroll — frosted, like the base pages' bar. */
      .sticky {
        position: relative;
        padding: 0;
        z-index: 3;
        flex: none;
        /*
         * 52px INCLUDING the border, matching .base-page-sticky-header exactly.
         *
         * This declared no height at all, so it was .head (52px) plus its own 1px border — 53px,
         * against every page's 52. The conversation header sat one pixel lower than the header of
         * the page beside it, which is small enough to look like a rendering artefact and constant
         * enough to be noticed every time.
         *
         * box-sizing has to be said HERE rather than relied on: a shadow root does not inherit the
         * document's global reset, so the default inside this component is content-box and a border
         * adds to a declared height. The .head rule already declares it; this did not.
         */
        box-sizing: border-box;
        height: 52px;
        /* A BAR, from the first frame. It used to be transparent until the thread scrolled under it,
           which meant a new conversation had a header you could read but not see — no edge, no
           separation from the transcript, and nothing marking the top of the panel. The tint and the
           rule are what make it a bar rather than a line of text floating above the messages. */
        background-color: color-mix(in srgb, var(--bg-primary) 92%, transparent);
        border-bottom: 1px solid var(--border-color);
        transition:
          background-color 0.2s ease,
          border-color 0.2s ease;
      }
      /* Once content is passing UNDER it, the bar goes translucent and blurs — so the transcript is
         visibly continuing behind it rather than ending at it. */
      .sticky.over {
        background-color: color-mix(in srgb, var(--bg-primary) 60%, transparent);
        backdrop-filter: blur(12px);
      }
      /* Fills the bar's content box (51px once the border is taken), instead of keeping its own
         52px and overflowing by exactly the border it was already double-counting. */
      .sticky .head {
        height: 100%;
      }
      .sticky-title {
        font-size: 14px;
        font-weight: 500;
        color: var(--text-primary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* ── the transcript and the composer ── */
      .log {
        flex: 1;
        overflow-y: auto;
        padding: 4px 16px 16px;
        min-height: 0;
        /*
         * SCROLL ANCHORING OFF — this is what made a collapsing card jump.
         *
         * The browser tries to keep whatever you are looking at stationary when content above it
         * changes height, by adjusting scrollTop. That is the right instinct for a page loading
         * images and exactly wrong for a 220ms height animation: it re-anchors on every frame, so
         * the transcript is scrolled a little on each one and the card appears to jitter rather than
         * to close. The transcript already decides its own scroll (stick-to-bottom on new content),
         * so the browser second-guessing it has nothing to add.
         */
        overflow-anchor: none;
      }
      /* The resumed-thread placeholder. Deliberately quiet and non-animated beyond a slow pulse:
         it is on screen for a few hundred milliseconds and a busy shimmer draws more attention to
         the wait than the wait deserves. */
      /* The windowed transcript. The outer box is the WHOLE conversation's height so the scrollbar
         is honest; each built row is placed at its own measured offset inside it. */
      .vwindow {
        position: relative;
        width: 100%;
      }
      .vrow {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
      }
      .skeleton {
        display: flex;
        flex-direction: column;
        gap: 18px;
        padding: 16px 0;
      }
      .sk-row {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .sk-bar {
        height: 10px;
        border-radius: 5px;
        background: var(--vs-border, rgba(127, 127, 127, 0.25));
        animation: sk-pulse 1.4s ease-in-out infinite;
      }
      .sk-bar.short {
        width: 55%;
      }
      @keyframes sk-pulse {
        0%, 100% { opacity: 0.45; }
        50% { opacity: 0.8; }
      }
      @media (prefers-reduced-motion: reduce) {
        .sk-bar { animation: none; }
      }
      .open-pipes {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        margin: 2px 0 6px 6px;
        padding: 3px 9px;
        font: inherit;
        font-size: 11px;
        cursor: pointer;
        border: 1px solid var(--border-color);
        border-radius: 999px;
        background: none;
        color: var(--text-secondary);
      }
      .open-pipes:hover {
        color: var(--text-primary);
        border-color: var(--blue);
      }
      .empty {
        color: var(--text-secondary);
        font-size: 13px;
        line-height: 1.5;
      }
      .dock {
        display: flex;
        /* Bottom, not centre: as the box grows the controls stay level with its last line, which is
           where the person is typing. Centred, Send drifts up the side of a tall box. */
        align-items: flex-end;
        gap: 8px;
        padding: 12px;
        border-top: 1px solid var(--border-color);
        flex-shrink: 0;
      }
      /* The text on the left, and everything that ACTS on it in one column on the right: the two
         small controls above the primary button. Two controls at two ends of the box read as two
         places to look; one column reads as one. */
      .rail {
        display: flex;
        flex-direction: column;
        /* STRETCH, so both rows are the rail's width and their left edges line up. Right-aligned,
           the top row was as wide as its contents and the button under it as wide as its label, so
           the two started at different x and the corner read as ragged. */
        align-items: stretch;
        justify-content: flex-end;
        gap: 6px;
        flex-shrink: 0;
      }
      /*
       * ADD, AND THE LEVERS, ON ONE LINE ABOVE THE PRIMARY BUTTON.
       *
       * There was a lone "+" floating over Talk with nothing beside it and nothing under it — a
       * control with no shape, doing two unrelated jobs (bring a file in, change how the turn runs)
       * behind one glyph that promised neither. Two controls now: a labelled Add for the file, and
       * the levers for the settings, sized to the row they sit on.
       */
      .rail-top {
        display: flex;
        align-items: center;
        /* Add against the rail's left edge — level with the primary button under it — and the levers
           against its right. The two rows then share both edges. */
        justify-content: space-between;
        gap: 6px;
      }
      /* The primary control fills the rail, so its left edge is Add's and its right edge is the
         levers'. A block, not a chip floating in a corner. */
      .rail > vitesse-button {
        width: 100%;
      }
      .draftwrap {
        flex: 1 1 auto;
        min-width: 0;
      }
      /* No height override here. size="lg" IS 36px, and forcing the HOST taller than the button
         inside it drew the host's box around a smaller bordered button — two rectangles, one
         control. The size scale is the knob; reaching past it was the bug. */

      @media (prefers-reduced-motion: reduce) {
        .sticky {
          transition: none;
        }
      }

      /*
       * THE RESPONSIVE RULES GO LAST, and that is not tidiness.
       *
       * A container query adds NO specificity — it only gates. So a breakpoint hiding .head-metrics,
       * written above the plain rule that displays it, loses to that rule on source order, and every
       * breakpoint here was dead: at 300px the group still drew, clipped to 36px, which is the
       * sliced-in-half badge the breakpoints exist to prevent. They come after what they override.
       */
      @container (max-width: 700px) {
        .head-metrics .badge:nth-child(n + 5) {
          display: none;
        }
      }
      @container (max-width: 620px) {
        .head-metrics .badge:nth-child(n + 4) {
          display: none;
        }
      }
      @container (max-width: 540px) {
        .head-metrics .badge:nth-child(n + 3) {
          display: none;
        }
      }
      @container (max-width: 460px) {
        .head-metrics .badge:nth-child(n + 2) {
          display: none;
        }
      }
      @container (max-width: 380px) {
        .head-metrics {
          display: none;
        }
      }
      /*
       * AND THE LABELLED CONTROLS BECOME THEIR ICONS.
       *
       * A column of the stack is 300px at its narrowest, and by then the badges have already gone:
       * what is left is the chevron, the title's own floor, up to two icon buttons and two LABELLED
       * buttons — Stop and Delete — which do not shrink. They were pushed outside the panel, which
       * is not "hidden", it is gone: the one irreversible control on the surface, unreachable.
       *
       * The label is what goes, not the control. The trash and the octagon are the icons the whole
       * app already uses for these two things, so the meaning survives the collapse — and the title
       * attribute is still the full sentence for anyone who hovers.
       */
      @container (max-width: 440px) {
        .head .del span {
          display: none;
        }
      }

      /*
       * AND AT THE COLUMN'S FLOOR, THE NAME GIVES WAY — because the controls cannot.
       *
       * The title carries an 84px floor so badges can never squeeze it out of existence, and that is
       * right until the row is a chevron, a title, FOUR icon buttons and a trash in 260px. Then the
       * floor is the thing that does not fit: the row overflows its own padding and the delete
       * control ends up flat against the divider with nothing between them — the one irreversible
       * control on the surface, touching the wall of the column beside it.
       *
       * So at this width the floor drops to a few characters. The name still ellipses rather than
       * disappearing, the full title is on hover, and the 12px of air at the end of the row survives,
       * which is what makes the header look like it ends rather than like it was cut.
       */
      @container (max-width: 340px) {
        .head {
          gap: 6px;
        }
        .head .head-name {
          min-width: 36px;
        }
      }
    `,
    ]; }
    /**
     * The draft, and the fact that it OUTLIVES the tab.
     *
     * Written through to local storage on every change rather than on unload: a closed tab never runs
     * cleanup, and "I closed it by accident" is exactly the case worth surviving. Four places set the
     * draft — typing, list continuation, branch, send — so it goes through one setter; a plain field
     * would have meant remembering to persist at each of them, and the one that got forgotten would
     * be the one that lost work.
     */
    /**
     * Choose who answers, and take their settings with them.
     *
     * A specialist is not just a prompt: it was configured with a model and a
     * thinking effort that suit the job it does. Applying those on pick is what
     * makes choosing one a single decision rather than three — and leaving the
     * model where it was would silently run a specialist tuned for a big-context
     * model on whatever the last thread happened to use.
     *
     * They stay EDITABLE afterwards. The specialist's settings are a starting
     * point, not a lock; the rows beneath the picker still work, so someone who
     * wants the program analyst thinking harder can say so.
     *
     * Going back to Conversation restores nothing on purpose: there is no previous
     * state worth resurrecting, and quietly changing the model a second time —
     * after the person had seen and possibly adjusted it — is the more surprising
     * of the two behaviours.
     */
    pickSpecialist(c, id) {
        c.expertId = id;
        const spec = this.specialists.find((e) => e.id === id);
        if (spec) {
            if (spec.model)
                c.model = spec.model;
            if (spec.effort)
                c.effort = spec.effort;
        }
        this.requestUpdate();
    }
    setDraft(text) {
        this.draft = text;
        const id = this.conversation?.id;
        const surface = this.surface;
        if (id && surface)
            saveDraft(surface, id, text);
    }
    /**
     * The first thing the PERSON said in this thread, for the header's label when there is no title.
     *
     * The person's own words, not an agent's note and not the assistant's reply: a peer note carries
     * `fromRunId` and sits in the same slot, so taking "the first user-role turn" would occasionally
     * name a thread after something another agent said in it. Truncated to a label's length here
     * rather than in the renderer, so the header and a list row that shows the same thread agree.
     */
    firstSaid() {
        const first = this.conversation?.turns.find((x) => x.role === 'user' && !x.fromRunId && !x.returned && x.text.trim());
        const text = (first?.text ?? '').replace(/\s+/g, ' ').trim();
        return text.length > 80 ? `${text.slice(0, 79)}…` : text;
    }
    willUpdate(changed) {
        if (changed.has('conversation')) {
            changed.get('conversation')?.removeEventListener('change', this.onChange);
            this.conversation?.addEventListener('change', this.onChange);
            /*
             * The box refills with whatever was left in THIS thread.
             *
             * Assigned to the field directly rather than through `setDraft`: restoring is not a change to
             * persist, and writing it back would re-stamp the draft's timestamp on every open — which
             * would keep an abandoned sentence alive forever by the act of looking at the thread.
             */
            const id = this.conversation?.id;
            this.draft = id && this.surface ? readDraft(this.surface, id) : '';
        }
    }
    connectedCallback() {
        super.connectedCallback();
        catalogEvents.addEventListener('change', this.onCatalog);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        catalogEvents.removeEventListener('change', this.onCatalog);
        this.conversation?.removeEventListener('change', this.onChange);
    }
    updated() {
        this.autosizeDraft();
        this.followLiveThinking();
        // Only follow when the reader was already at the bottom. Scrolling someone back to the live edge
        // while they are reading history is the layout shift they did not ask for.
        if (this.stickToBottom)
            this.pinToBottom();
        // And when they are NOT at the bottom, hold the row they are reading still — see `holdAnchor`.
        else
            this.holdAnchor();
    }
    /**
     * The box grows to fit what is in it, up to the cap in CSS.
     *
     * MEASURED, not counted. It used to set `rows` from the number of newlines, which is wrong for the
     * common case: a paragraph typed without pressing enter wraps across six lines and reports one, so
     * the composer stayed a single row no matter how much was in it. Only layout knows how many lines
     * text occupies, so the height is read back from layout.
     *
     * Collapsed to `auto` first, because `scrollHeight` on an already-tall box reports the box, not
     * the content — without that it can grow and never shrink.
     *
     * This is the one legitimate inline style in the file: a height that depends on measurement is not
     * a styling decision, and no CSS expresses it portably (`field-sizing: content` does, but is not
     * in every browser this ships to). The cap stays in CSS, where it belongs.
     */
    autosizeDraft() {
        const ta = this.renderRoot.querySelector('.draft');
        if (!ta)
            return;
        ta.style.height = 'auto';
        ta.style.height = `${ta.scrollHeight}px`;
    }
    /** `renderRoot`, not `this` — the template lives in the shadow root, so a light-DOM query finds
     *  nothing and the transcript silently stops following the live edge. */
    pinToBottom() {
        const log = this.renderRoot.querySelector('[data-log]');
        if (log)
            log.scrollTop = log.scrollHeight;
    }
    followBottom(ms) {
        if (!this.stickToBottom)
            return;
        this.followUntil = Math.max(this.followUntil, performance.now() + ms);
        if (this.following)
            return;
        this.following = true;
        const step = () => {
            if (!this.stickToBottom || performance.now() > this.followUntil) {
                this.following = false;
                return;
            }
            this.pinToBottom();
            requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }
    /**
     * Keep an OPEN, LIVE thinking panel scrolled to its newest line.
     *
     * The panel is a fixed-height scroller, and thinking is long — a measured DeepSeek turn produced
     * 5,425 characters over 50 seconds. Without this, opening it live shows the first few lines and
     * then never moves again, which reads as frozen rather than as thinking.
     *
     * Only while LIVE, and only when already at the bottom, for the same reason the transcript itself
     * follows conditionally: once the thought has settled the panel is a document someone is reading,
     * and yanking it is the layout shift nobody asked for.
     */
    followLiveThinking() {
        // Only while something is still thinking: a settled thought is a document someone is reading.
        // A thought's disclosure is a row in the shared registry now (see renderThought), so "open" is
        // read off the DOM rather than off a set of turn objects.
        if (!this.conversation?.turns.some((t) => t.thinking) && !this.streaming.parts.some((p) => p.kind === 'reasoning'))
            return;
        for (const el of this.renderRoot.querySelectorAll('.think-fold.open .think-body')) {
            const box = el;
            if (box.scrollHeight - box.scrollTop - box.clientHeight < 60)
                box.scrollTop = box.scrollHeight;
        }
    }
    /**
     * The header's contents — identical inline and stuck.
     *
     * `compact` only tightens the type and drops the back label to its chevron, because the stuck bar
     * is 52px of a narrow rail and the title is what matters there.
     */
    header(compact) {
        const m = this.conversation?.metrics;
        return html `
      <div class="head ${compact ? 'compact' : ''}">
        <!-- The shared lead: the blue chevron, then what you are looking at. The same template the
             agent tree's header uses, so stepping between them moves nothing.
             The label rule is every list row's. The preview comes from the thread in hand: its
             first user turn is already rendered a few lines below, so a header that said "Untitled"
             above it was failing to read what was on its own screen. -->
        ${headLead({
            ...(this.onBack ? { onBack: () => this.onBack?.() } : {}),
            ...(this.onBackHover ? { onBackHover: () => this.onBackHover?.() } : {}),
            backLabel: t('ai.backToConversations'),
            title: conversationLabel({ title: this.convTitle, preview: this.firstSaid() }),
            compact,
        })}
        <!--
          THE MEASUREMENTS, IN ONE GROUP THAT CAN GIVE WAY.

          They were loose children of the row, each flex-shrink:0, so a header with four of them
          simply grew past its own box and pushed Delete off the right-hand edge — the control was
          not hidden, it was outside the panel. A group that may shrink to nothing is what stops the
          row overflowing at all; the container queries below then drop whole badges rather than
          leaving one sliced in half.

          Only once a turn has completed. A 0 ms badge would be a lie rather than a placeholder.
          BLUE, not gray: these are live measurements of this thread, not dormant metadata, and
          gray is the tone this app uses for the inert.
        -->
        <div class="head-metrics">
        <!-- WHAT THIS AGENT IS DOING, before what it spent doing it. Green only for a run that
             declared success: a step-capped or budget-stopped run is finished and is not done, and
             painting those green reports a failure as an achievement. -->
        ${this.agent
            ? html `<span class="badge"
                >${statusBadge(this.agent.state === 'done' ? 'green' : this.agent.state === 'stopped' ? 'red' : this.agent.state === 'running' ? 'blue' : 'yellow', runStateLabel(this.agent.state))}</span
              >`
            : nothing}
        <!-- How long it took, for a run. A person's thread has no such span — it is as long as they
             have had it open — so this appears only for an agent. -->
        <!-- THE STOP, beside the state it ends. A running agent had no way to be stopped from its
             own thread — the verb existed on the server and nothing offered it — so a person
             watching an agent go wrong could only close the tab, which orphans the run rather than
             ending it. Red, the way every "turn this off" control is. -->
        ${this.agent?.elapsed ? html `<span class="badge">${statusBadge('blue', this.agent.elapsed)}</span>` : nothing}
        ${m?.ttftMs !== undefined ? html `<span class="badge">${statusBadge('blue', `${t('ai.ttft')} ${fmtMs(m.ttftMs)}`)}</span>` : nothing}
        ${m?.avgMs !== undefined ? html `<span class="badge">${statusBadge('blue', `${t('ai.avgLatency')} ${fmtMs(m.avgMs)}`)}</span>` : nothing}
        <!-- What the thread has cost so far. Shown only once a provider has actually reported a
             figure: a free model that cost nothing and a provider that says nothing about cost are
             different facts, and "$0.00" for the second is a number nobody measured. -->
        ${m?.costUsd !== undefined
            ? html `<span class="badge">${statusBadge('blue', fmtUsd(m.costUsd))}</span>`
            : this.agent?.cost
                ? html `<span class="badge">${statusBadge('blue', this.agent.cost)}</span>`
                : nothing}
        <!-- What the thread has SPENT of a context window and of the bill. Absent when the provider
             did not report usage — a thread nobody measured has not used zero tokens. -->
        ${m?.tokens !== undefined
            ? html `<span class="badge">${statusBadge('blue', fmtTokens(m.tokens))}</span>`
            : this.agent?.tokens
                ? html `<span class="badge">${statusBadge('blue', this.agent.tokens)}</span>`
                : nothing}
        </div>
        <span class="head-spacer"></span>
        <vitesse-ai-chapters .chapters=${this.chapters} .onJump=${(seq) => this.jumpTo(seq)}></vitesse-ai-chapters>
        ${
        /*
         * THE WAY ONTO THE CANVAS, in the header rather than beside a tree.
         *
         * It used to sit inline under each spawned tree, which put a control that changes SURFACE
         * inside a row's content, and repeated it once per spawn. It acts on the whole thread, so
         * it belongs with the thread's other controls — the platform's shared icon button, so it
         * carries the same flat-blue-on-hover treatment as every other icon in the app instead of
         * a spelling of its own.
         */
        this.onOpenTree && (this.treeRoot || this.spawnedRoot())
            ? html `<button
                class="icon-btn"
                title=${t('ai.pipes')}
                aria-label=${t('ai.pipes')}
                @click=${() => this.onOpenTree?.(this.treeRoot || this.spawnedRoot(), { from: this.nodeRunId })}
              >
                <vitesse-icon name="network" size="15"></vitesse-icon>
              </button>`
            : nothing}
        ${
        // WHAT IT MADE, next to the way to what it did. An agent whose answer is a program gets a
        // door to it here; one whose answer is a sentence does not, because the host only supplies
        // this when there is something behind it.
        this.agent?.onOpenArtifact
            ? html `<button
                class="icon-btn"
                title=${t('ai.openArtifact')}
                aria-label=${t('ai.openArtifact')}
                @click=${() => this.agent?.onOpenArtifact?.()}
              >
                <vitesse-icon name="monitor-play" size="15"></vitesse-icon>
              </button>`
            : nothing}
        ${this.agent?.onStop && this.runInFlight
            ? // The owner's stop, beside Delete and built exactly like it: the same platform button,
                // red, with the octagon that means "stop" — so the two controls that end something
                // read as a pair rather than as a badge next to a button.
                html `<vitesse-button
                class="del"
                color="red"
                size="sm"
                icon="octagon-minus"
                title=${t('ai.stopRun')}
                @click=${() => this.agent?.onStop?.()}
                ><span>${t('ai.stop')}</span></vitesse-button
              >`
            : this.conversation?.busy
                ? // And the SAME control, in the same corner, for a plain conversation's own turn: a
                    // reply being written is stopped from the header exactly as an agent is, so the eye
                    // finds Stop in one place whatever kind of thread this is.
                    html `<vitesse-button
                  class="del"
                  color="red"
                  size="sm"
                  icon="octagon-minus"
                  title=${t('ai.stop')}
                  @click=${() => this.conversation?.stop()}
                  ><span>${t('ai.stop')}</span></vitesse-button
                >`
                : nothing}
        ${this.onDelete
            ? // A LABELLED button, not a bare glyph. Deleting a conversation is the one irreversible
                // thing on this surface, and a lone trash icon in a header row of badges reads as
                // another status until it is clicked. The platform's own button, so it looks like
                // every other destructive control in the app rather than like a bespoke one here.
                html `<vitesse-button
                class="del"
                color="red"
                size="sm"
                icon="trash-2"
                title=${t('ai.deleteConversation')}
                @click=${() => this.onDelete?.()}
                ><span>${t('ai.deleteShort')}</span></vitesse-button
              >`
            : nothing}
      </div>
    `;
    }
    captureAnchor(el) {
        const top = el.scrollTop;
        const row = this.vrows.rows.find((r) => r.start + r.size > top);
        this.anchor = row ? { index: row.index, start: row.start } : undefined;
    }
    holdAnchor() {
        if (this.stickToBottom || !this.anchor)
            return;
        const row = this.vrows.rows.find((r) => r.index === this.anchor.index);
        if (!row || row.start === this.anchor.start)
            return;
        const log = this.renderRoot.querySelector('.log');
        if (!log)
            return;
        log.scrollTop += row.start - this.anchor.start;
        this.anchor = { index: row.index, start: row.start };
    }
    /**
     * Send — or, when a branch is pending, MAKE the branch.
     *
     * Clicking Branch does not create anything: it puts the message back in the composer and waits.
     * That is the whole point of a branch — you are asking the question differently, and you cannot
     * say how until you have edited it. Creating the branch on the click would make an identical copy
     * and leave the editing to be done afterwards, in a thread you would then have to find.
     *
     * Sending is what commits it. Escape, or emptying the box, abandons it with nothing written.
     */
    /**
     * Scroll to the turn a chapter begins at.
     *
     * By SEQ, which is the same address a branch and a pin use — the transcript is keyed by position
     * everywhere, so navigation costs no extra state. A chapter whose turn is not loaded yet scrolls
     * to the top of what IS loaded rather than doing nothing, so the control always answers.
     */
    jumpTo(seq) {
        const turns = this.conversation?.turns ?? [];
        const at = turns.findIndex((x) => x.seq === seq);
        const log = this.renderRoot.querySelector('.log');
        const rows = log?.querySelectorAll('.turn');
        const target = at >= 0 ? rows?.[at] : rows?.[0];
        this.stickToBottom = false;
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    /** Leave branch mode without making one. The box is cleared, because its contents were a copy of
     *  a message that already exists — keeping them would look like a draft the person wrote. */
    cancelBranch() {
        this.pendingBranch = null;
        this.setDraft('');
    }
    /** The seq of this thread's first message — what turns a branch's stored ordinal into a seq. */
    get firstSeq() {
        for (const t of this.conversation?.turns ?? [])
            if (typeof t.seq === 'number')
                return t.seq;
        return 0;
    }
    async submit() {
        const text = this.draft;
        if (!text.trim() || !this.conversation || this.conversation.busy)
            return;
        const branch = this.pendingBranch;
        this.setDraft('');
        this.pendingBranch = null;
        this.stickToBottom = true;
        if (branch !== null) {
            await this.onBranchSubmit?.(branch, text);
            return;
        }
        const files = this.files.map((f) => f.file);
        this.files = [];
        this.rejected = '';
        await this.conversation.send(text, 'user', '', files);
    }
    /**
     * One turn, drawn by the SHARED renderer.
     *
     * The markup lives in `turn-view` because a bubble is not this view's private business — the
     * transcript detail view and a sub-agent's thread show the same thing, and a second copy is two
     * designs drifting. What stays here is what is genuinely this view's: which disclosures are open,
     * and the agent trees, which only a live conversation can resolve.
     */
    renderTurn(turn) {
        return renderTurnShared(turn, this.turnHandlers);
    }
    /** Whether the agent this thread belongs to is still working. A person's own thread has no agent
     *  and is never in flight by this measure — its streaming state lives on the conversation. */
    get runInFlight() {
        return !!this.agent && this.agent.state !== 'done' && this.agent.state !== 'stopped';
    }
    /** Whether the agent is doing something right now, as opposed to waiting. A parked agent — on a
     *  person, on its children, or on a time it asked to be woken at — is unfinished but idle, and its
     *  composer is how the person talks to it; putting Stop where Send and Talk live told them the
     *  opposite. Stopping it is still offered, from the header, where stopping always is. */
    get runWorking() {
        return this.runInFlight && this.agent?.state !== 'awaiting';
    }
    /**
     * IS THIS THREAD WORKING? — and on an agent's thread the RUN decides, not this tab.
     *
     * A conversation's `busy` is a fact about a turn THIS TAB started. On a person's own thread that
     * is the whole truth: nothing else drives it. On an AGENT's thread it is a second opinion about
     * something the server already knows, and the two disagreed in the worst possible way — the header
     * said "Stopped", from the run, while the composer said "Thinking…" with a Stop button under it,
     * from a turn that had been abandoned. One surface, two answers, and the wrong one was the one
     * with the controls on it.
     *
     * So where there is a run, the run wins. A stopped or finished agent is not thinking, whatever
     * this tab has left over; and because the state is read from the tree on every render rather than
     * held here, a reload cannot bring the contradiction back — there is nowhere for it to survive.
     */
    get threadBusy() {
        const c = this.conversation;
        if (!c)
            return false;
        if (this.agent)
            return this.runInFlight && c.busy;
        return c.busy;
    }
    get turnHandlers() {
        const seqOf = (turn) => turn.seq ?? 0;
        return {
            // The POSITION travels; the work does not happen here. A view that called the archive would be
            // doing IO (the Cardinal Rule), and the host already holds the manager that owns it.
            onFork: this.onForkTurn ? (turn) => this.onForkTurn?.(seqOf(turn)) : undefined,
            onPin: this.onPinTurn ? (turn) => this.onPinTurn?.(seqOf(turn), chapterTitle(turn.text)) : undefined,
            // Retry IS the conversation's own operation — unlike fork and pin, which write to the archive
            // through the host's manager, this re-runs the thread in place and needs nothing the view does
            // not already hold. Offered only on the trailing answer, because that is the only turn a retry
            // can replace without rewriting history.
            // By SEQ, plus the thread's first seq — see branches.ts. A turn's index is not a message
            // index (folding merges tool messages into the turn they belong to), and the two sides of a
            // split do not share a seq space, so the branch's anchor is derived from its own first seq.
            versions: (turn) => (typeof turn.seq === 'number' ? (this.versionsAt?.(turn.seq, this.firstSeq) ?? null) : null),
            onVersion: (id) => this.onOpenVersion?.(id),
            onBranch: (turn) => {
                if (typeof turn.seq !== 'number')
                    return;
                this.pendingBranch = turn.seq;
                this.setDraft(turn.text);
                // Focus and select, so the first keystroke replaces it — the common case is asking the same
                // thing another way, not appending to it.
                void this.updateComplete.then(() => {
                    const ta = this.renderRoot.querySelector('.draft');
                    ta?.focus();
                    ta?.select();
                });
            },
            onRetry: (turn) => {
                if (turn === this.conversation?.turns.at(-1))
                    void this.conversation?.retryLast();
            },
            loadArtifact: this.loadArtifact,
            /*
             * A RUN'S LAST TURN IS NOT FINISHED WHILE THE RUN IS NOT.
             *
             * A watched thread is rebuilt from the archive, and every archived turn arrives settled —
             * so the trailing one drew its fork/copy/retry row the moment it landed, a step boundary
             * read as the end, and then the next step appeared underneath it. "It acts as if it is
             * done and then starts back up on its own." The run knows whether it is done; the turn does
             * not, so the run decides.
             */
            // EVERY turn of a live run, not only the last: earlier turns are steps of the same job, and
            // fork/copy/retry on a step of work still in progress is noise that reads as an ending.
            inFlight: () => this.runInFlight,
            toolOpen: (id) => this.openTools.has(id),
            quiet: this.density === 'context',
            onToggleTool: (id) => this.toggleTool(id),
            toolExtra: () => nothing,
            toolAction: (tool) => html `${this.renderCancelWait(tool)}${this.renderOpenAgent(tool)}`,
            // The RETURN row's way out, so the spawn and its answer offer the same door. The run that
            // sent it is the address: the tree read resolves a whole forest from any run in it, so the
            // child's own id names both the canvas to load and the card to land on.
            returnAction: (turn) => this.renderOpenReturned(turn),
            /*
             * WHAT THE SENDER OF A MESSAGE IS CALLED.
             *
             * A peer's message arrives carrying the sender's run id and nothing else — a transcript is a
             * list of messages and does not know what a run is. The name lives in the run tree, which this
             * view has and `turn-view` deliberately does not: a transcript renderer that reached for the
             * tree would only work on the surface that happens to have one. So the answer comes from here,
             * and a host without a tree returns nothing and the row falls back to saying an agent sent it.
             */
            agentName: (runId) => this.agentNames?.(runId) ?? '',
            /*
             * WHETHER A SPAWNED AGENT IS STILL AT WORK — answered from the tree this thread follows.
             *
             * The conversation already holds every tree a turn started (`followSpawned`) and re-emits
             * their changes as its own, so the row's tense updates as the agent advances with no extra
             * subscription. The host's answer is the fallback, for a surface whose trees live elsewhere.
             */
            agentRunning: (runId, rootRunId) => {
                const tree = this.conversation?.tree(rootRunId);
                const run = tree?.run(runId);
                if (run)
                    return run.status !== RunStatus.FINISHED;
                // A tree we follow but have not read yet is still WORKING as far as anyone knows — a row
                // that said "Spawned" for the second between the call returning and the first tree read,
                // then "Spawning…" again, was reporting that gap as a finish.
                if (tree?.loading)
                    return true;
                return this.agentRunning?.(runId);
            },
        };
    }
    /**
     * The agent trees this turn started.
     *
     * Under the tool rows, because the spawn IS one of those rows and the tree is what it produced —
     * a subtree hanging off the call that created it, rather than a separate region of the transcript
     * that the reader has to connect back by themselves.
     */
    /**
     * The agents ONE spawn call started, drawn inside that call's disclosure.
     *
     * This used to hang off the turn, after every tool row, with a loose "Pipes" button beside it —
     * so a spawn produced a block that sat below the whole exchange, in a style shared with nothing
     * around it. The tree is a subtree of the call that created it, so it is drawn there, and it
     * inherits the row's own open/closed state rather than carrying a second one.
     *
     * The route to the canvas is no longer here: it is a header control, because it acts on the whole
     * thread rather than on one row, and a button that changes surface does not belong inside a
     * disclosure the reader opened to look at arguments.
     */
    /** The tree this thread's header opens — the first agent it spawned. A thread with several roots
     *  still has one canvas: the tree read resolves the whole forest from any run in it, so the first
     *  is a complete address rather than an arbitrary pick among several. */
    spawnedRoot() {
        for (const turn of this.conversation?.turns ?? []) {
            for (const tool of turn.tools ?? []) {
                const root = rootRunIdOfTool(tool.data);
                if (root)
                    return root;
            }
        }
        return '';
    }
    /*
     * THE KILL BUTTON ON A WAIT — shown only while the wait is still open.
     *
     * A closed tab is not how a person stops an agent that is waiting to act at 3am; this is. It ends
     * the wait in the same call the agent opened, so the agent is told in words at its next step, and
     * the server refuses a third wait after two cancels in a row — the agent cannot simply outlast the
     * person pressing it. `unknown` counts as open: after a reload a wait still waiting has no outcome
     * recorded, which is exactly what `unknown` means.
     */
    renderCancelWait(tool) {
        if (!isWaitTool(tool.name) || (tool.state !== 'running' && tool.state !== 'unknown'))
            return nothing;
        const conversationId = this.conversation?.id;
        if (!conversationId)
            return nothing;
        const busy = this.cancellingWaits.has(tool.id);
        return html `<button
      class="icon-btn"
      title=${t('ai.cancelWait')}
      aria-label=${t('ai.cancelWait')}
      ?disabled=${busy}
      @click=${async (e) => {
            e.stopPropagation();
            if (this.cancellingWaits.has(tool.id))
                return;
            this.cancellingWaits.add(tool.id);
            this.requestUpdate();
            const refused = await cancelWait(conversationId, tool.id);
            this.cancellingWaits.delete(tool.id);
            if (refused)
                console.warn('[wait] cancel refused:', refused);
            this.requestUpdate();
        }}
    >
      <vitesse-icon name="ban" size="14"></vitesse-icon>
    </button>`;
    }
    renderOpenAgent(tool) {
        const root = rootRunIdOfTool(tool.data);
        if (!root || !this.conversation)
            return nothing;
        const tree = this.conversation.tree(root);
        if (!tree)
            return nothing;
        return html `<button
      class="icon-btn"
      title=${t('ai.openAgent')}
      aria-label=${t('ai.openAgent')}
      @click=${(e) => {
            e.stopPropagation();
            // The forest to load, the card to start from (this thread's own), and the agent to travel
            // to — out, across, and in. The one gesture that is ABOUT the shape of the tree.
            this.onOpenTree?.(root, { from: this.nodeRunId, focus: spawnedRunIdOfTool(tool.data), enter: true });
        }}
    >
      <vitesse-icon name="external-link" size="14"></vitesse-icon>
    </button>`;
    }
    /** The way into the agent whose answer this row is. Absent when the turn does not name a run —
     *  an older archived return, which has nowhere to send anybody. */
    renderOpenReturned(turn) {
        const run = turn.fromRunId;
        if (!run)
            return nothing;
        /*
         * WHAT IT MADE, beside the way to what it did — on the row that says it came back.
         *
         * A widget author's answer is a program, and the row announcing it was a disclosure over a JSON
         * string. Opening the artifact from here is the same gesture as opening the agent, one step
         * shorter: the reader is already looking at the sentence that says it finished.
         */
        const open = this.openArtifact?.(run);
        return html `${open
            ? html `<button
            class="icon-btn"
            title=${t('ai.openArtifact')}
            aria-label=${t('ai.openArtifact')}
            @click=${(e) => {
                e.stopPropagation();
                open();
            }}
          >
            <vitesse-icon name="monitor-play" size="14"></vitesse-icon>
          </button>`
            : nothing}${this.onOpenTree
            ? html `<button
            class="icon-btn"
            title=${t('ai.openAgent')}
            aria-label=${t('ai.openAgent')}
            @click=${(e) => {
                e.stopPropagation();
                this.onOpenTree?.(run, { from: this.nodeRunId, focus: run, enter: true });
            }}
          >
            <vitesse-icon name="external-link" size="14"></vitesse-icon>
          </button>`
            : nothing}`;
    }
    /**
     * The turns, whole or windowed.
     *
     * The windowed branch places each rendered turn at its measured offset inside a container sized
     * to the whole transcript, so the scrollbar still describes the entire conversation and
     * stick-to-bottom (`scrollTop = scrollHeight`) keeps working untouched.
     */
    /** The assignment, in the position the reader's own first message would be. */
    renderBrief() {
        const b = this.brief;
        if (!b || !b.objective)
            return nothing;
        return html `<div class="turn brief">
      <div class="bubble">
        <!-- SIGNED. This is a MESSAGE — it sits where the reader's own first message would, and it
             is drawn as one, in the same bubble every message in this thread gets. What it is not
             is the reader's: an agent's parent wrote it, and without a signature the operator reads
             their own words above an answer they never asked for, and the agent's transcript claims
             a human said something no human said. So the message is generic and the byline is not. -->
        <div class="brief-sig">
          <vitesse-icon name="sparkles" size="12"></vitesse-icon>
          <span>${b.from ? t('ai.briefFrom', { name: b.from }) : t('ai.briefFromAgent')}</span>
        </div>
        <div class="brief-body">${renderMarkdown(b.objective)}</div>
        ${b.deliverable ? html `<div class="brief-deliv">${renderMarkdown(b.deliverable)}</div>` : nothing}
      </div>
    </div>`;
    }
    /**
     * The answer being written, drawn as the turn it is about to become.
     *
     * Through the SAME renderer as a real turn — markdown, code fences, the lot — so the moment the
     * durable message replaces it nothing on screen changes shape. A caret rather than a skeleton:
     * the reader is watching words arrive, which is a different thing from waiting.
     */
    renderTurns(turns) {
        if (turns.length < VIRTUALIZE_ABOVE) {
            // Not windowed, and the controller is left unattached so it observes nothing.
            return turns.map((x) => this.renderTurn(x));
        }
        this.vrows.setCount(turns.length);
        return html `<div class="vwindow" style="height:${this.vrows.totalSize}px">
      ${this.vrows.rows.map((r) => {
            const turn = turns[r.index];
            return turn
                ? html `<div class="vrow" data-index=${r.index} style="transform:translateY(${r.start}px)" ${ref(this.vrows.measure)}>
              ${this.renderTurn(turn)}
            </div>`
                : nothing;
        })}
    </div>`;
    }
    /* Keyed by CALL ID, not by the object: a tool row is replaced when its outcome lands, so an
     * identity-keyed set would silently close every row the moment it resolved. */
    toggleTool(id) {
        const next = new Set(this.openTools);
        if (!next.delete(id))
            next.add(id);
        this.openTools = next;
        this.followBottom(FOLD_MS);
    }
    /*
     * THE APPROVAL CARD — an Ask-tier tool call, waiting on this user.
     *
     * Inline in the transcript rather than a modal, because it IS part of the conversation: the model
     * asked to do something and the turn is paused on the answer. A modal over the whole app for a
     * question scoped to one thread would also block the user from scrolling up to check the context
     * that decides their answer.
     *
     * The ARGUMENTS are shown, readably. Approving a call you cannot see is not approval, it is a
     * button — the entire value of the gate is that the person knows what they said yes to, and the
     * audit row records that a person who could see the arguments did.
     */
    renderConfirm(c) {
        let args = [];
        try {
            const parsed = JSON.parse(c.argumentsJson || '{}');
            // Nested values are laid out, not dumped: a person approving a WRITE reads the path and the
            // value on their own lines, not a bracketed JSON row they have to parse under time pressure.
            args = Object.entries(parsed).map(([k, v]) => [k, prettyArg(v)]);
        }
        catch {
            // Malformed JSON still renders raw: hiding what the model actually sent is the one wrong move
            // on a card whose job is showing what is about to run.
            args = c.argumentsJson ? [['arguments', c.argumentsJson]] : [];
        }
        return html `
      <div
        style="margin-bottom:12px;padding:10px 12px;border-radius:12px;border:1px solid var(--border-color);background:var(--bg-secondary)"
      >
        <div style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-secondary)">
          <vitesse-icon name="shield-alert" size="15"></vitesse-icon>${t('ai.confirmNeeded')}
        </div>
        <div style="margin-top:6px;font-size:14px;font-weight:500;color:var(--text-primary)" title=${c.name}>
          ${toolDisplayName(c.name)}
        </div>
        ${args.length
            ? html `<div
                style="margin-top:6px;font-size:12.5px;line-height:1.5;color:var(--text-secondary);white-space:pre-wrap;word-break:break-word"
              >
                ${args.map(([k, v]) => v.includes('\n')
                ? html `<div><span style="color:var(--text-tertiary)">${k}:</span><div style="margin:2px 0 6px 12px;font-family:var(--font-mono, ui-monospace, monospace);font-size:12px">${v}</div></div>`
                : html `<div><span style="color:var(--text-tertiary)">${k}:</span> ${v}</div>`)}
              </div>`
            : nothing}
        <div style="display:flex;gap:8px;margin-top:10px">
          <button
            @click=${() => this.onConfirm?.(c.id, true)}
            style="padding:6px 14px;border-radius:8px;border:none;background:var(--blue);color:#fff;font-size:13px;cursor:pointer"
          >
            ${t('ai.confirmApprove')}
          </button>
          <button
            @click=${() => this.onConfirm?.(c.id, false)}
            style="padding:6px 14px;border-radius:8px;border:1px solid var(--border-color);background:transparent;color:var(--text-primary);font-size:13px;cursor:pointer"
          >
            ${t('ai.confirmDeny')}
          </button>
        </div>
      </div>
    `;
    }
    render() {
        const c = this.conversation;
        if (!c)
            return html `<div style="padding:24px;color:var(--text-secondary);font-size:14px">${t('ai.noConversation')}</div>`;
        return html `
      <div class="shell">
        <!--
          ONE header, ALWAYS THERE.

          It used to be rendered twice — an inline copy at the top of the thread and a sticky copy
          that slid down once the inline one scrolled away. Which meant a short conversation, or one
          you had not scrolled yet, had no header bar at all: the title, the timings and the delete
          control only appeared once there was enough transcript to push them off. A chrome that
          arrives when the content gets long is a chrome that is missing exactly when someone is
          getting their bearings in a new thread.

          So there is one, pinned. It still fades its background in on scroll, because a solid bar
          over the top of a thread that has not moved is heavier than it needs to be — but the bar,
          and everything in it, is present from the first frame.
        -->
        <div class="sticky ${this.scrolled ? 'over' : ''}">${this.header(true)}</div>

        <div
          data-log
          class="log"
          @scroll=${this.onScroll}
          @artifact-measured=${() => this.followBottom(200)}
          @wheel=${this.onHand}
          @touchstart=${this.onHand}
          @touchmove=${this.onHand}
          @keydown=${this.onHand}
          ${ref(this.vrows.attach)}
        >
          ${c.turns.length === 0
            ? // A thread whose past is still in flight is NOT an empty thread. Opening is instant
                // (the manager hands the conversation over before the archive answers), so without
                // this the one honest-looking thing on screen would be a sentence saying the
                // conversation is empty — about a conversation that is not.
                // The same skeleton the panel draws while an agent's thread is fetched: one moment,
                // one picture, and neither surface gets to invent its own.
                c.hydrating
                    ? transcriptSkeleton()
                    : /*
                       * AN AGENT'S EMPTY THREAD IS ITS BRIEF, NEVER THE GREETING.
                       *
                       * An agent writes its first turn only when its first model call RETURNS — tens of
                       * seconds on a reasoning model, and longer on a first step that reads a lot. For
                       * all of it `turns` is empty, so this fell to the greeting: "Good afternoon, Max —
                       * ask about anything here, attach a file, or just talk", under a header reading
                       * "Analyse mock-938637234be8 PLC program · Running · 15s".
                       *
                       * Three things wrong at once, and they compound: the surface invited a person to
                       * start a conversation that had already started, it said nothing about what the
                       * agent had been asked to do, and it made a working agent look like an empty box
                       * for the entire time it was doing the hardest part of its job.
                       *
                       * The brief is available from the first FRAME — it is the run's own task, which
                       * the tree already holds — so an agent's thread opens on what it was asked, with
                       * the skeleton beneath it standing in for the answer that is coming. Which is what
                       * the thread looks like a moment later anyway.
                       */
                        this.brief?.objective
                            ? html `${this.renderBrief()}${this.streaming.parts.length ? this.renderTurns(liveThread([], this.streaming, this.runInFlight)) : transcriptSkeleton()}`
                            : greetingView(this.userName, this.greetSeed)
            : /* ONE thread: the archive, with what the run is writing continuing its last turn.
                 The overlay was a turn of its own underneath, and every part re-mounted when its
                 step landed — see live-thread.ts. */
                html `${this.renderBrief()}${this.renderTurns(liveThread(c.turns, this.streaming, this.runInFlight))}`}
          <!-- At the FOOT of the transcript: the newest thing in the conversation is the question. -->
          ${this.confirms.map((x) => this.renderConfirm(x))}
        </div>

        <!--
          BRANCHING IS A MODE, so it says so.
          
          Clicking Branch fills the box and waits for Send — which, with no notice, looks exactly
          like nothing happening: the text appears in a composer that may already have had text in
          it, and the next Send quietly makes a version instead of a reply. The bar names the state
          and offers the way out of it.
        -->
        ${this.pendingBranch !== null
            ? html `<div class="branching">
                <vitesse-icon name="git-branch" size="13"></vitesse-icon>
                <span>${t('ai.branchingFrom')}</span>
                <button class="branch-cancel" @click=${() => this.cancelBranch()}>${t('common.cancel')}</button>
              </div>`
            : nothing}
        <div class="dock">
          <!--
            THE SAME CONTROLS AS THE OPENING COMPOSER, mid-conversation.
          
            A choice that can only be made before the first message is a choice made with the
            least information — you learn a question needs a deeper model by asking it and
            watching the answer. Model, thinking effort and attachments are per-TURN, read at
            send time, so changing one applies to the next turn and leaves the turns already
            on screen at the effort they were actually run at.
          -->
          <div class="draftwrap">
            ${renderChips(this.files, this.rejected, (id) => (this.files = this.files.filter((x) => x.id !== id)))}
            <textarea
              class="draft"
              rows="1"
              .value=${this.draft}
              @input=${(e) => {
            this.setDraft(e.target.value);
        }}
              @keydown=${this.onKey}
              ?disabled=${this.threadBusy}
              placeholder=${this.threadBusy ? t('ai.thinking') : t('ai.composerPlaceholder')}
            ></textarea>
          </div>
          <div class="rail">
          <div class="rail-top">
          <!-- Bringing a file in is its own act, with its own word on it. It used to be a row
               inside the settings menu, two clicks behind a glyph that said nothing about files. -->
          <vitesse-button size="sm" icon="plus" title=${t('ai.attach')} @click=${() => this.picker?.click()}
            ><span>${t('ai.add')}</span></vitesse-button
          >
          <vitesse-ai-tools
            .textModels=${TEXT_MODELS}
            .voiceModels=${REALTIME_MODELS}
            .imageModels=${this.defaultImageModel ? IMAGE_MODELS : []}
            .imageDefault=${this.defaultImageModel}
            .textValue=${c.model}
            .textDefault=${this.defaultChatModel}
            .voiceDefault=${this.defaultVoiceModel}
            .effort=${c.effort}
            .onTextModel=${(v) => {
            c.model = v;
            this.requestUpdate();
        }}
            .onEffort=${(v) => {
            c.effort = v;
            this.requestUpdate();
        }}
            .specialists=${this.specialists}
            .specialist=${c.expertId}
            .onSpecialist=${(id) => this.pickSpecialist(c, id)}
            .onAttach=${() => this.picker?.click()}
          ></vitesse-ai-tools>
          </div>
          <input type="file" multiple accept=${ACCEPT_ATTR} @change=${this.onPick} />
          <!--
            ONE control, three states — stop while a turn runs, send when there is something to
            send, talk when there is not.
            
            It was a disabled Send sitting there whenever the box was empty, which is the state the
            composer is in most of the time: the primary control spent most of its life greyed out
            saying no. Talk is what you actually do with an empty box.
          -->
          ${this.threadBusy
            ? html `<vitesse-button size="lg" icon="octagon-minus" @click=${() => c.stop()}>${t('ai.stop')}</vitesse-button>`
            : this.agent?.onStop && this.runWorking && !this.draft.trim()
                ? html `<vitesse-button size="lg" icon="octagon-minus" title=${t('ai.stopRun')} @click=${() => this.agent?.onStop?.()}>${t('ai.stop')}</vitesse-button>`
                : this.draft.trim()
                    ? html `<vitesse-button size="lg" icon="send" title=${t('ai.send')} @click=${() => void this.submit()}
                    >${t('ai.send')}</vitesse-button
                  >`
                    : html `<vitesse-button
                    size="lg"
                    icon="mic-signal"
                    title=${t('ai.startVoice')}
                    ?disabled=${!this.onStartVoice}
                    @click=${() => this.onStartVoice?.()}
                    >${t('ai.talk')}</vitesse-button
                  >`}
          </div>
        </div>
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "conversation", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "loadArtifact", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "specialists", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onBack", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onBackHover", void 0);
__decorate([
    property()
], VitesseAiChat.prototype, "convTitle", void 0);
__decorate([
    property()
], VitesseAiChat.prototype, "userName", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onDelete", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "confirms", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onConfirm", void 0);
__decorate([
    property()
], VitesseAiChat.prototype, "defaultChatModel", void 0);
__decorate([
    property()
], VitesseAiChat.prototype, "defaultVoiceModel", void 0);
__decorate([
    property()
], VitesseAiChat.prototype, "defaultImageModel", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onOpenConversation", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "agent", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onOpenTree", void 0);
__decorate([
    property()
], VitesseAiChat.prototype, "nodeRunId", void 0);
__decorate([
    property()
], VitesseAiChat.prototype, "treeRoot", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "agentNames", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "agentRunning", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "openArtifact", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "brief", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "streaming", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onForkTurn", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onPinTurn", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "versionsAt", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onOpenVersion", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onBranchSubmit", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "onStartVoice", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChat.prototype, "chapters", void 0);
__decorate([
    state()
], VitesseAiChat.prototype, "pendingBranch", void 0);
__decorate([
    state()
], VitesseAiChat.prototype, "files", void 0);
__decorate([
    state()
], VitesseAiChat.prototype, "rejected", void 0);
__decorate([
    query('input[type=file]')
], VitesseAiChat.prototype, "picker", void 0);
__decorate([
    state()
], VitesseAiChat.prototype, "draft", void 0);
__decorate([
    state()
], VitesseAiChat.prototype, "scrolled", void 0);
__decorate([
    property()
], VitesseAiChat.prototype, "surface", void 0);
__decorate([
    property()
], VitesseAiChat.prototype, "density", void 0);
__decorate([
    state()
], VitesseAiChat.prototype, "openTools", void 0);
VitesseAiChat = __decorate([
    customElement('vitesse-ai-chat')
], VitesseAiChat);
export { VitesseAiChat };
/**
 * One argument, laid out for a person: scalars inline, objects as `key: value` lines, and an array
 * of objects as one block per item — so `writes: [{"path":"DB10.Motor1.Running","value":true}]`
 * reads as "path DB10.Motor1.Running / value true".
 */
function prettyArg(v, depth = 0) {
    if (v === null || v === undefined)
        return '';
    if (typeof v !== 'object')
        return String(v);
    const pad = '  '.repeat(depth);
    if (Array.isArray(v)) {
        if (v.every((x) => typeof x !== 'object' || x === null))
            return v.map((x) => String(x)).join(', ');
        return v.map((x, i) => `${pad}${i + 1}.\n${prettyArg(x, depth + 1)}`).join('\n');
    }
    return Object.entries(v)
        .map(([k, x]) => (typeof x === 'object' && x !== null ? `${pad}${k}:\n${prettyArg(x, depth + 1)}` : `${pad}${k}: ${prettyArg(x)}`))
        .join('\n');
}
/**
 * Tokens the way a person reads them: exact while the number is small enough to mean something,
 * thousands once it is not. "12.4k tok" answers "how big is this thread" — "12,431" does not answer
 * it any better and takes longer to read.
 */
function fmtTokens(n) {
    return n < 1000 ? `${n} tok` : `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k tok`;
}
/** Latency the way a person reads it: milliseconds until they stop being meaningful, then seconds. */
function fmtMs(ms) {
    return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}
/**
 * A CHAPTER'S TITLE, from the message it marks.
 *
 * The message is already the best description of itself, so the title is its opening sentence. The
 * alternative — asking a model to name it — turns an instant control into a round trip, puts a cost
 * on a bookmark, and sends the text of the conversation to a provider to label it. None of those is
 * worth paying for a line of text the reader can already see.
 *
 * The first SENTENCE rather than the first N characters, so it ends where a person would have
 * ended it; only a very long one is cut, and then visibly.
 */
export function chapterTitle(text) {
    const flat = text.replace(/\s+/g, ' ').trim();
    if (!flat)
        return '';
    const stop = flat.search(/[.!?](\s|$)/);
    const first = stop > 0 ? flat.slice(0, stop + 1) : flat;
    return first.length <= 60 ? first : `${first.slice(0, 57).trimEnd()}…`;
}
//# sourceMappingURL=chat.js.map